import type React from 'react';
import {useState} from 'react';
import {Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField} from '@mui/material';
import {useDebouncedSearch} from '~/hooks/useDebouncedSearch';
import {useUserSearchQuery} from '~/pages/supplier-360/tabs/SupplierOverview/hooks/useUserSearchQuery';
import {type SupplierContactUser, supplierContactUserName} from '~/pages/supplier-360/types/Supplier360Types';
import type {TeamMemberInput} from '../../types/categoryRecordTypes';

const USER_OPTION_COUNT = 20;

type Props = {
    title: string;
    submitLabel: string;
    roles: string[];
    raci: string[];
    /** RACI the dialog starts on (the category owner is Accountable). */
    defaultRaci?: string;
    defaultRole: string;
    /** The member and the user's name (for the notice). */
    onSubmit: (input: TeamMemberInput, name: string) => Promise<unknown>;
    onClose: () => void;
};

const RACI_HELP: Record<string, string> = {
    Responsible: 'Does the work on the category.',
    Accountable: 'Answers for the category and signs off decisions.',
    Consulted: 'Gives input before decisions are made.',
    Informed: 'Is kept up to date on decisions.',
};

/**
 * The shared add-person modal: used for "Add new category owner" and "Add team member". Mount it to open it. People are
 * picked from the app's accounts, searched on the shared `/users/` endpoint (the only place users are read from); the
 * team stores no names or emails of its own.
 */
export const PersonDialog: React.FC<Props> = ({title, submitLabel, roles, raci, defaultRole, defaultRaci = 'Consulted', onSubmit, onClose}) => {
    // Server-side search, debounced: `/users/` is paginated, so the term narrows the pool, never a client-side filter.
    const {debounced: search, setInput: setInputValue} = useDebouncedSearch();
    const {data: pool = [], isFetching} = useUserSearchQuery(search, USER_OPTION_COUNT, true);
    const [user, setUser] = useState<SupplierContactUser | null>(null);
    // The picked user stays an option when a later search no longer returns them.
    const users = user && !pool.some(u => u.id === user.id) ? [user, ...pool] : pool;
    const [role, setRole] = useState(defaultRole);
    const [raciValue, setRaci] = useState(defaultRaci);
    const [busy, setBusy] = useState(false);

    const submit = () => {
        if (!user) return;
        setBusy(true);
        onSubmit({userId: user.id, role, raci: raciValue}, supplierContactUserName(user))
            .then(onClose)
            .catch(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="cp-person-title">
            <DialogTitle id="cp-person-title">{title}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <Autocomplete
                        options={users} loading={isFetching} value={user} onChange={(_, v) => setUser(v)}
                        filterOptions={options => options}
                        onInputChange={(_, v, reason) => reason !== 'reset' && setInputValue(v)}
                        getOptionLabel={u => u.email ? `${supplierContactUserName(u)} (${u.email})` : supplierContactUserName(u)}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={params => (
                            <TextField {...params} label="User of the app" required autoFocus size="small"
                                       helperText="People on the team are users of the app who can open this dataset."/>
                        )}
                    />
                    <TextField select label="Role" value={role} onChange={e => setRole(e.target.value)} size="small">
                        {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </TextField>
                    <TextField select label="RACI" value={raciValue} onChange={e => setRaci(e.target.value)} size="small"
                               helperText={RACI_HELP[raciValue]}>
                        {raci.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !user}>
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
